// deploy-token + bind-token — both finalize a token-management step on the
// omni-bridge NEAR contract using a light-client proof of an EVM-side event:
//
//   deploy-token : proves a `LogMetadata` event on the source EVM chain,
//                  then `deploy_token(chain_kind=Near, prover_args)` mints
//                  the bridged token contract on NEAR.
//   bind-token   : proves a `DeployToken` event on the source EVM chain,
//                  then `bind_token(chain_kind=<source>, prover_args)` links
//                  the freshly-deployed token back to the bridge.
//
// Both take Borsh `prover_args` (an EVM light-client proof), so the unsigned
// NEAR tx carries `args` as raw bytes — signed via the same wallet path as
// near-fin-transfer (see sdkTx.ts). Solana/Starknet sources use MPC paths
// bridge-sdk-js doesn't expose; we reject them with a clear bridge-cli hint.

import { ChainKind } from "@omni-bridge/core";
import type { EvmChainKind } from "@omni-bridge/core";
import { createNearBuilder } from "@omni-bridge/near";
import { ProofKind } from "@omni-bridge/near";
import { getEvmProof } from "@omni-bridge/evm";

import { nearView } from "@/api/client";
import { chains } from "@/config/chains";
import type { Network } from "@/config/network";
import type { UnsignedNearTx } from "@/types/dto";
import { omniBridgeContract, FinTransferUnsupported } from "@/wallet/finTransfer";
import { sdkTxToUnsignedNearTx } from "@/wallet/sdkTx";

/** EVM source chains we can prove client-side (have a NEAR light client or
 *  Wormhole path the SDK's `getEvmProof` covers). Eth is the canonical
 *  light-client source; the L2s also work through `getEvmProof`. Abs/Strk
 *  bind/deploy go through MPC and are rejected. */
type EvmKey = "eth" | "arb" | "base" | "bnb" | "pol";

const EVM_SOURCES: Record<EvmKey, { chainKind: ChainKind; prefix: string }> = {
  eth: { chainKind: ChainKind.Eth, prefix: "eth" },
  arb: { chainKind: ChainKind.Arb, prefix: "arb" },
  base: { chainKind: ChainKind.Base, prefix: "base" },
  bnb: { chainKind: ChainKind.Bnb, prefix: "bnb" },
  pol: { chainKind: ChainKind.Pol, prefix: "pol" },
};

/** Form's PascalCase ChainKind name → our EVM key. */
const NAME_TO_EVM_KEY: Record<string, EvmKey> = {
  Eth: "eth",
  Arb: "arb",
  Base: "base",
  Bnb: "bnb",
  Polygon: "pol",
};

export interface TokenProofArgs {
  network: Network;
  /** PascalCase source chain (where the LogMetadata/DeployToken tx lives). */
  sourceChain: string;
  /** Source-chain tx hash. */
  txHash: string;
  signerId: string;
}

function resolveEvmSource(sourceChain: string): { key: EvmKey; chainKind: ChainKind } {
  const key = NAME_TO_EVM_KEY[sourceChain];
  if (!key) {
    throw new FinTransferUnsupported(
      `${sourceChain} source isn't supported client-side — deploy/bind from Solana or ` +
        `Starknet uses an MPC proof that bridge-sdk-js doesn't expose. Run bridge-cli for those.`,
    );
  }
  return { key, chainKind: EVM_SOURCES[key].chainKind };
}

// ─── deploy-token ───────────────────────────────────────────────────────────

export async function buildDeployToken(args: TokenProofArgs): Promise<{ tx: UnsignedNearTx }> {
  const { network, txHash, signerId } = args;
  const { key, chainKind } = resolveEvmSource(args.sourceChain);
  const rpcUrl = chains(network).evm[key].rpcUrl;

  // LogMetadata(address,string,string,uint8) is the event deploy_token proves.
  const { toEventSelector } = await import("viem");
  const topic = toEventSelector("LogMetadata(address,string,string,uint8)");
  const proof = await getEvmProof(
    txHash as `0x${string}`,
    topic,
    chainKind as unknown as EvmChainKind,
    network,
    rpcUrl,
  );

  const builder = createNearBuilder({ network });
  const proverArgs = builder.serializeEvmProofArgs({ proof_kind: ProofKind.LogMetadata, proof });
  const deposit = await requiredBalance("required_balance_for_deploy_token", 4n * 10n ** 24n);
  // deploy_token mints the bridged token ONTO NEAR → destinationChain = Near.
  const unsigned = builder.buildDeployToken(ChainKind.Near, proverArgs, signerId, deposit);
  return { tx: sdkTxToUnsignedNearTx(unsigned) };
}

// ─── bind-token ─────────────────────────────────────────────────────────────

export async function buildBindToken(args: TokenProofArgs): Promise<{ tx: UnsignedNearTx }> {
  const { network, txHash, signerId } = args;
  const { key, chainKind } = resolveEvmSource(args.sourceChain);
  const rpcUrl = chains(network).evm[key].rpcUrl;

  // DeployToken(address,string,string,string,uint8,uint8) is the event bind proves.
  const { toEventSelector } = await import("viem");
  const topic = toEventSelector("DeployToken(address,string,string,string,uint8,uint8)");
  const proof = await getEvmProof(
    txHash as `0x${string}`,
    topic,
    chainKind as unknown as EvmChainKind,
    network,
    rpcUrl,
  );

  const builder = createNearBuilder({ network });
  const proverArgs = builder.serializeEvmProofArgs({ proof_kind: ProofKind.DeployToken, proof });
  const deposit = await requiredBalance("required_balance_for_bind_token", 10n ** 23n);
  // bind_token's chain_kind is the SOURCE chain (the token's origin).
  const unsigned = builder.buildBindToken(chainKind, proverArgs, signerId, deposit);
  return { tx: sdkTxToUnsignedNearTx(unsigned) };
}

/** Query a `required_balance_for_*` view on the omni-bridge contract,
 *  falling back to a sane floor when the view is absent or returns 0. */
async function requiredBalance(method: string, floor: bigint): Promise<bigint> {
  const omni = await omniBridgeContract();
  try {
    const raw = await nearView<string | number>(omni, method, {});
    const v = BigInt(typeof raw === "number" ? Math.trunc(raw) : raw);
    return v > 0n ? v : floor;
  } catch {
    // Older contracts may not export the view — use the documented floor.
    return floor;
  }
}
